import { useEffect, useMemo, useState } from 'react';
import {
  findVariant,
  formatMoney,
  isOptionValueAvailable,
  thumbLetter,
  useCatalogProduct,
} from './catalog';
import { ComboSteps, useComboWizard } from './ComboPicker';
import { comboPreviewImage, useComboData } from './combo';
import { IconBack, IconVoucher } from './icons';
import { JudgeMeReviews, JudgeMeSummary } from './JudgeMeReviews';
import { displayCompare, livePriceDisplay, useProductQuote } from './pricing';
import { useSwipeClose } from './SwipeSheet';
import { VoucherChips } from './VoucherChips';
import { LiveProduct, LiveSessionDiscount, comboDiscountId } from './utils';

interface Props {
  apiUrl: string;
  product: LiveProduct;
  discounts?: LiveSessionDiscount[];
  liveSessionId?: string;
  isLive?: boolean;
  views?: number;
  onClose: () => void;
  onBack?: () => void;
  onAddToCart: (productId: string, variantId: string) => Promise<void>;
  onBuyNow: (productId: string, variantId: string) => Promise<void>;
  onAddCombo: (
    productId: string,
    lines: Array<{ variantId: string; quantity: number }>
  ) => Promise<void>;
  onBuyCombo: (
    productId: string,
    lines: Array<{ variantId: string; quantity: number }>
  ) => Promise<void>;
  products?: LiveProduct[];
  onSelectProduct?: (p: LiveProduct) => void;
}

export function ProductSheet({
  apiUrl,
  product,
  discounts = [],
  liveSessionId,
  isLive,
  views = 0,
  onClose,
  onBack,
  onAddToCart,
  onBuyNow,
  onAddCombo,
  onBuyCombo,
  products = [],
  onSelectProduct,
}: Props) {
  const info = useCatalogProduct(product);
  const comboId = comboDiscountId(product);
  const { data: comboData, loading: comboLoading } = useComboData(apiUrl, comboId);
  const wizard = useComboWizard(comboData);
  const swipeClose = useSwipeClose(onClose);
  const handleClose = () => swipeClose();
  const handleBack = () => {
    if (onBack) onBack();
    else handleClose();
  };
  const [imageIdx, setImageIdx] = useState(0);
  const [selections, setSelections] = useState<(string | null)[]>([null, null, null]);
  const [busy, setBusy] = useState<'add' | 'buy' | null>(null);
  const [showDesc, setShowDesc] = useState(false);

  useEffect(() => {
    setImageIdx(0);
    setShowDesc(false);
    setSelections([null, null, null]);
  }, [info?.id]);

  const selectedVariant = useMemo(() => {
    if (!info) return null;
    return findVariant(info, selections) || info.variants[0] || null;
  }, [info, selections]);

  const quote = useProductQuote(
    apiUrl,
    product,
    comboId ? undefined : selectedVariant?.id,
    liveSessionId,
    comboId && wizard.complete ? wizard.lines : null
  );
  const compare = displayCompare(
    quote,
    info,
    selectedVariant?.compareAt != null ? Number(selectedVariant.compareAt) : null
  );

  const images = useMemo(() => {
    if (!info) return [] as string[];
    const list = [...info.images];
    const featured = selectedVariant?.featuredImage;
    if (featured && !list.includes(featured)) list.unshift(featured);
    if (!list.length && info.image) list.push(info.image);
    if (!list.length) {
      const comboImage = comboPreviewImage(comboData);
      if (comboImage) list.push(comboImage);
    }
    return list;
  }, [info, selectedVariant?.featuredImage, comboData]);

  useEffect(() => {
    if (selectedVariant?.featuredImage) {
      const idx = images.indexOf(selectedVariant.featuredImage);
      if (idx >= 0) setImageIdx(idx);
    }
  }, [selectedVariant?.id]);

  const selectOption = (optionIndex: number, value: string) => {
    if (!info) return;
    const next = [...selections] as (string | null)[];
    next[optionIndex] = value;
    setSelections(next);
  };

  const add = async () => {
    if (!selectedVariant?.id) return;
    try {
      setBusy('add');
      await onAddToCart(product.item_id, selectedVariant.id);
    } finally {
      setBusy(null);
    }
  };

  const buy = async () => {
    if (!selectedVariant?.id) return;
    try {
      setBusy('buy');
      await onBuyNow(product.item_id, selectedVariant.id);
    } finally {
      setBusy(null);
    }
  };

  const addCombo = async () => {
    if (!wizard.complete) return;
    try {
      setBusy('add');
      await onAddCombo(product.item_id, wizard.lines);
    } finally {
      setBusy(null);
    }
  };

  const buyCombo = async () => {
    if (!wizard.complete) return;
    try {
      setBusy('buy');
      await onBuyCombo(product.item_id, wizard.lines);
    } finally {
      setBusy(null);
    }
  };

  const meaningfulOptions = (info?.options || []).filter(
    (o) => o.values.length > 1 || (o.values[0] && o.values[0] !== 'Default Title')
  );

  // Combo placeholders carry the combo total as their own price / compare-at
  const comboPrice = comboId
    ? comboData?.whole_discount_value ??
      (info?.price != null ? Number(info.price) : null)
    : null;
  const comboCompare =
    comboId && info?.compareAt != null && comboPrice != null && Number(info.compareAt) > comboPrice
      ? Number(info.compareAt)
      : null;
  const displaySlash = comboId ? comboCompare ?? compare : compare;
  const nowPrice = quote
    ? quote.finalTotal
    : comboPrice != null
      ? comboPrice
      : selectedVariant?.price != null
        ? Number(selectedVariant.price)
        : null;
  const strikes = livePriceDisplay(nowPrice, quote, displaySlash);

  return (
    <div className="il-sheet il-sheet-tall il-pdp-sheet" role="dialog">
      <div className="il-sheet-grab" aria-hidden>
        <div className="il-sheet-handle" />
      </div>
      <button type="button" className="il-sheet-back" onClick={handleBack} aria-label="Back">
        <IconBack size={20} />
      </button>

      <div className="il-pdp-scroll">
        <div className="il-pdp-gallery">
          <div className="il-pdp-main-img">
            {images[imageIdx] ? (
              <img src={images[imageIdx]} alt="" />
            ) : (
              <div className="il-pdp-ph">{thumbLetter(info?.title || product.item_name)}</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="il-pdp-thumbs">
              {images.map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  type="button"
                  className={`il-pdp-thumb${i === imageIdx ? ' is-active' : ''}`}
                  onClick={() => setImageIdx(i)}
                >
                  <img src={src} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="il-pdp-body-block">
          <div className="il-pdp-price-row">
            <span className="il-price">
              {strikes.now != null ? `RM ${formatMoney(strikes.now)}` : '—'}
            </span>
            {strikes.preLive != null && (
              <span className="il-price-was-live">
                RM {formatMoney(strikes.preLive)}
              </span>
            )}
            {strikes.compare != null && (
              <span className="il-price-was-compare">
                RM {formatMoney(strikes.compare)}
              </span>
            )}
            {quote && quote.voucherDiscount > 0 && (
              <span className="il-price-voucher" title="Voucher savings">
                <IconVoucher size={18} />
                <span>−RM {formatMoney(quote.voucherDiscount)}</span>
              </span>
            )}
          </div>
          <h3 className="il-pdp-title">{info?.title || product.item_name || product.item_id}</h3>
          {!comboId && <JudgeMeSummary apiUrl={apiUrl} productId={product.item_id} />}
          <div className="il-bag-stats">
            {isLive && <span className="il-stat-live">LIVE now</span>}
            {views > 0 && <span>{views} viewed</span>}
            {quote?.freeShipping && <span className="il-stat-ship">Free shipping</span>}
          </div>
          <VoucherChips
            discounts={discounts}
            className="il-pdp-vouchers"
            appliedDiscountIds={quote?.appliedDiscountIds}
            appliedTitles={quote?.appliedTitles}
            appliedDiscounts={quote?.appliedDiscounts}
            stackPlus
            products={products}
            currentProductId={product.item_id}
            onOpenProduct={onSelectProduct}
          />
        </div>

        {comboId ? (
          comboLoading ? (
            <p className="il-empty">Loading combo…</p>
          ) : comboData ? (
            <ComboSteps wizard={wizard} />
          ) : (
            <p className="il-empty">This combo is not available right now.</p>
          )
        ) : null}

        {!comboId &&
          meaningfulOptions.map((opt, optionIndex) => (
            <div key={opt.name} className="il-option-block">
              <div className="il-option-label">
                {opt.name}
                {selections[optionIndex] ? (
                  <span className="il-option-picked">: {selections[optionIndex]}</span>
                ) : null}
              </div>
              <div className="il-option-pills">
                {opt.values.map((val) => {
                  const available = info
                    ? isOptionValueAvailable(info, optionIndex, val, selections)
                    : false;
                  const active = selections[optionIndex] === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      className={`il-option-pill${active ? ' is-active' : ''}${
                        !available ? ' is-disabled' : ''
                      }`}
                      onClick={() => selectOption(optionIndex, val)}
                    >
                      {val}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

        {info?.bodyHtml ? (
          <div className="il-pdp-desc">
            <button
              type="button"
              className="il-pdp-desc-toggle"
              onClick={() => setShowDesc((v) => !v)}
            >
              {showDesc ? 'Hide description' : 'Product description'}
            </button>
            {showDesc && (
              <div
                className="il-pdp-desc-body"
                dangerouslySetInnerHTML={{ __html: info.bodyHtml }}
              />
            )}
          </div>
        ) : null}

        {!comboId && <JudgeMeReviews apiUrl={apiUrl} productId={product.item_id} />}
      </div>

      {comboId ? (
        <div className="il-pdp-actions il-combo-actions">
          {wizard.currentStep > 0 && (
            <button
              type="button"
              className="il-btn-outline il-combo-back"
              disabled={busy != null}
              onClick={wizard.back}
            >
              ← Back
            </button>
          )}
          {wizard.complete ? (
            <>
              <button
                type="button"
                className="il-btn-outline"
                disabled={busy != null}
                onClick={addCombo}
              >
                {busy === 'add' ? (
                  <>
                    <span className="il-btn-spinner il-btn-spinner-dark" aria-hidden />
                    Adding…
                  </>
                ) : (
                  `Add to cart • RM ${formatMoney(
                    quote?.finalTotal ?? comboPrice
                  )}`
                )}
              </button>
              <button
                type="button"
                className="il-btn-buy"
                disabled={busy != null}
                onClick={buyCombo}
              >
                {busy === 'buy' ? (
                  <>
                    <span className="il-btn-spinner" aria-hidden />
                    Buying…
                  </>
                ) : (
                  'Buy now'
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="il-btn-buy"
              disabled={busy != null || !wizard.canAdvance || !comboData}
              onClick={wizard.next}
            >
              Next →
            </button>
          )}
        </div>
      ) : (
        <div className="il-pdp-actions">
          <button
            type="button"
            className="il-btn-outline"
            disabled={busy != null || !selectedVariant?.id || selectedVariant.available === false}
            onClick={add}
          >
            {busy === 'add' ? (
              <>
                <span className="il-btn-spinner il-btn-spinner-dark" aria-hidden />
                Adding…
              </>
            ) : (
              'Add to cart'
            )}
          </button>
          <button
            type="button"
            className="il-btn-buy"
            disabled={busy != null || !selectedVariant?.id || selectedVariant.available === false}
            onClick={buy}
          >
            {busy === 'buy' ? (
              <>
                <span className="il-btn-spinner" aria-hidden />
                Buying…
              </>
            ) : (
              'Buy now'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
